import React, { useMemo, useState } from "react";
import { Typography, Space, Card, Steps, Form, message, Segmented } from "antd";
import {
  RobotOutlined,
  ApartmentOutlined,
  MessageOutlined,
  ApiOutlined,
  KeyOutlined,
  BookOutlined,
} from "@ant-design/icons";
import { supabase } from "../lib/supabase";
import { AdminNavBar } from "../components/AdminNavBar";
import { trackFeatureUsage } from "../lib/posthog";
import {
  getCompanyUsers,
  getUserCompanies,
  getUsers,
} from "../services/adminUsers";
import {
  BOOKING_INSIGHT_SELECT,
  BOOKING_LIST_SELECT,
  OPEN_BOOKING_STATUSES,
  PIPELINE_STEPS,
  buildBookingLookupOrFilter,
  normalizeEmail,
  normalizePhone,
  pickBestBookingMatch,
  phonesLikelyMatch,
  type BookingInsight,
  type BookingListRow,
  type BotInsightsMode,
  type CompanyMatchRow,
  type LookupResult,
  type MatchReason,
} from "../components/admin/bot/botAdminShared";
import {
  enrichBotBooking,
  fetchEnrichedBookingById,
} from "../components/admin/bot/enrichBotBooking";
import { BotResolveStep } from "../components/admin/bot/BotResolveStep";
import { BotCompanyStep } from "../components/admin/bot/BotCompanyStep";
import { BotInsightsStep } from "../components/admin/bot/BotInsightsStep";
import { BotReplyStep } from "../components/admin/bot/BotReplyStep";
import { BotSampleConversation } from "../components/admin/bot/BotSampleConversation";
import { PartnerApiDocs } from "../components/admin/bot/PartnerApiDocs";
import { PartnerApiKeysPanel } from "../components/admin/bot/PartnerApiKeysPanel";
import { PartnerApiRequestLogsPanel } from "../components/admin/bot/PartnerApiRequestLogsPanel";
import { PartnerApiGlossary } from "../components/admin/bot/PartnerApiGlossary";

const { Title, Text, Paragraph } = Typography;

type BotAdminView = "pipeline" | "sample" | "api" | "keys" | "glossary";

const BotAdmin: React.FC = () => {
  const [form] = Form.useForm<{ email?: string; phone?: string }>();
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [results, setResults] = useState<LookupResult[]>([]);
  const [matchedUserEmails, setMatchedUserEmails] = useState<string[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null,
  );
  const [currentStep, setCurrentStep] = useState(0);
  const [bookingQuery, setBookingQuery] = useState("");
  const [bookingLoading, setBookingLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [bookingSearched, setBookingSearched] = useState(false);
  const [bookingInsight, setBookingInsight] = useState<BookingInsight | null>(
    null,
  );
  const [insightsMode, setInsightsMode] = useState<BotInsightsMode>("lookup");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [bookingList, setBookingList] = useState<BookingListRow[]>([]);
  const [adminView, setAdminView] = useState<BotAdminView>("pipeline");

  const selectedResult = useMemo(
    () => results.find((r) => r.company.id === selectedCompanyId) ?? null,
    [results, selectedCompanyId],
  );

  const resetBookingState = () => {
    setBookingInsight(null);
    setBookingList([]);
    setBookingSearched(false);
  };

  const handleInsightsModeChange = (mode: BotInsightsMode) => {
    setInsightsMode(mode);
    resetBookingState();
  };

  const handleBookingLookup = async () => {
    if (selectedCompanyId == null) {
      message.warning("Select a company first.");
      return;
    }
    const raw = bookingQuery.trim();
    if (!raw) {
      message.warning("Enter a booking id or reference.");
      return;
    }

    setBookingLoading(true);
    setBookingSearched(true);
    setBookingInsight(null);
    setBookingList([]);
    trackFeatureUsage("admin_bot_booking_lookup");

    try {
      const orFilter = buildBookingLookupOrFilter(raw);
      const { data, error } = await supabase
        .from("bookings")
        .select(BOOKING_INSIGHT_SELECT)
        .eq("company_id", selectedCompanyId)
        .or(orFilter)
        .limit(20);

      if (error) throw error;

      const rows = (data ?? []) as unknown as BookingInsight[];
      const best = pickBestBookingMatch(rows, raw);

      if (!best) {
        message.info(
          "No booking found for this company with that id/reference.",
        );
        setBookingInsight(null);
        return;
      }

      setBookingInsight(await enrichBotBooking(best));
    } catch (err) {
      console.error("Bot booking lookup failed:", err);
      message.error(
        err instanceof Error ? err.message : "Booking lookup failed.",
      );
      setBookingInsight(null);
    } finally {
      setBookingLoading(false);
    }
  };

  const loadBookingList = async (opts: {
    statuses?: string[];
    analytics: string;
    limit?: number;
  }) => {
    if (selectedCompanyId == null) {
      message.warning("Select a company first.");
      return;
    }

    setListLoading(true);
    setBookingSearched(true);
    setBookingInsight(null);
    setBookingList([]);
    trackFeatureUsage(opts.analytics);

    try {
      let query = supabase
        .from("bookings")
        .select(BOOKING_LIST_SELECT)
        .eq("company_id", selectedCompanyId)
        .order("created_at", { ascending: false })
        .limit(opts.limit ?? 30);

      if (opts.statuses && opts.statuses.length > 0) {
        query = query.in("status", opts.statuses);
      }

      const { data, error } = await query;
      if (error) throw error;

      setBookingList((data ?? []) as unknown as BookingListRow[]);
      if (!data?.length) {
        message.info("No bookings found for this filter.");
      }
    } catch (err) {
      console.error("Bot booking list failed:", err);
      message.error(
        err instanceof Error ? err.message : "Failed to load bookings.",
      );
      setBookingList([]);
    } finally {
      setListLoading(false);
    }
  };

  const handleLoadRecent = () =>
    loadBookingList({
      statuses: [...OPEN_BOOKING_STATUSES],
      analytics: "admin_bot_booking_recent",
    });

  const handleLoadAll = () =>
    loadBookingList({
      analytics: "admin_bot_booking_all",
      limit: 100,
    });

  const handleLoadByStatus = () => {
    if (!statusFilter) {
      message.warning("Select a status first.");
      return;
    }
    return loadBookingList({
      statuses: [statusFilter],
      analytics: "admin_bot_booking_status",
    });
  };

  const handleSelectListBooking = async (bookingId: number) => {
    if (selectedCompanyId == null) return;
    setBookingLoading(true);
    trackFeatureUsage("admin_bot_booking_select");
    try {
      const enriched = await fetchEnrichedBookingById(
        bookingId,
        selectedCompanyId,
      );
      if (!enriched) {
        message.info("Booking not found for this company.");
        setBookingInsight(null);
        return;
      }
      setBookingInsight(enriched);
    } catch (err) {
      console.error("Bot booking select failed:", err);
      message.error(
        err instanceof Error ? err.message : "Failed to load booking.",
      );
      setBookingInsight(null);
    } finally {
      setBookingLoading(false);
    }
  };

  const handleSearch = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;

    const email = normalizeEmail(values.email ?? "");
    const phone = (values.phone ?? "").trim();

    if (!email && !phone) {
      message.warning("Enter an email and/or phone number.");
      return;
    }

    setLoading(true);
    setSearched(true);
    setSelectedCompanyId(null);
    setCurrentStep(0);
    trackFeatureUsage("admin_bot_lookup");

    try {
      const companyById = new Map<number, CompanyMatchRow>();

      const addCompany = (
        row: {
          id: number;
          name: string;
          status?: string | null;
          phone_number?: string | null;
          invoice_email?: string | null;
          city?: string | null;
          country?: string | null;
        },
        reason: MatchReason,
      ) => {
        const existing = companyById.get(row.id);
        if (existing) {
          if (!existing.matchReasons.includes(reason)) {
            existing.matchReasons.push(reason);
          }
          return;
        }
        companyById.set(row.id, {
          id: row.id,
          name: row.name,
          status: row.status,
          phone_number: row.phone_number,
          invoice_email: row.invoice_email,
          city: row.city,
          country: row.country,
          matchReasons: [reason],
        });
      };

      if (email || phone) {
        const orParts: string[] = [];
        if (email) orParts.push(`invoice_email.ilike.%${email}%`);
        if (phone) {
          const digits = normalizePhone(phone);
          orParts.push(`phone_number.ilike.%${phone}%`);
          if (digits && digits !== phone) {
            orParts.push(`phone_number.ilike.%${digits}%`);
          }
        }

        const { data: companies, error: companyError } = await supabase
          .from("company")
          .select("id, name, status, phone_number, invoice_email, city, country")
          .or(orParts.join(","))
          .limit(50);

        if (companyError) throw companyError;

        for (const row of companies ?? []) {
          const emailHit =
            !!email &&
            !!row.invoice_email &&
            normalizeEmail(row.invoice_email).includes(email);
          const phoneHit =
            !!phone && phonesLikelyMatch(row.phone_number, phone);

          if (emailHit) addCompany(row, "company_invoice_email");
          if (phoneHit) addCompany(row, "company_phone");
          if (!emailHit && !phoneHit) {
            if (email) addCompany(row, "company_invoice_email");
            else addCompany(row, "company_phone");
          }
        }
      }

      const hitUserEmails: string[] = [];
      if (email) {
        const { data: usersData, error: usersError } = await getUsers(
          1,
          50,
          email,
          "email",
          "ascend",
        );
        if (usersError) throw usersError;

        const users =
          (usersData as { users?: Array<{ id: string; email?: string }> })
            ?.users ?? [];

        for (const user of users) {
          if (!user.email) continue;
          if (!normalizeEmail(user.email).includes(email)) continue;
          hitUserEmails.push(user.email);

          const { data: ucData, error: ucError } = await getUserCompanies(
            user.id,
          );
          if (ucError) throw ucError;

          const links =
            (ucData as {
              user_companies?: Array<{
                company_id: number;
                company?: {
                  id: number;
                  name: string;
                  status?: string | null;
                  city?: string | null;
                  country?: string | null;
                } | null;
              }>;
            })?.user_companies ?? [];

          for (const link of links) {
            if (link.company) {
              addCompany(
                {
                  id: link.company.id,
                  name: link.company.name,
                  status: link.company.status,
                  city: link.company.city,
                  country: link.company.country,
                  phone_number: null,
                  invoice_email: null,
                },
                "user_email",
              );
            } else {
              const { data: companyRow, error: cErr } = await supabase
                .from("company")
                .select(
                  "id, name, status, phone_number, invoice_email, city, country",
                )
                .eq("id", link.company_id)
                .maybeSingle();
              if (cErr) throw cErr;
              if (companyRow) addCompany(companyRow, "user_email");
            }
          }
        }
      }

      for (const [id, row] of companyById) {
        if (row.phone_number != null && row.invoice_email != null) continue;
        const { data: full, error } = await supabase
          .from("company")
          .select("id, name, status, phone_number, invoice_email, city, country")
          .eq("id", id)
          .maybeSingle();
        if (!error && full) {
          companyById.set(id, {
            ...row,
            ...full,
            matchReasons: row.matchReasons,
          });
        }
      }

      const lookupResults: LookupResult[] = [];
      for (const company of companyById.values()) {
        const { data: cuData, error: cuError } = await getCompanyUsers(
          company.id,
        );
        if (cuError) {
          message.error(
            `Failed to load users for ${company.name}: ${String(cuError)}`,
          );
          lookupResults.push({ company, users: [] });
          continue;
        }

        const users =
          (
            cuData as {
              users?: Array<{
                id: string;
                email?: string;
                created_at?: string;
              }>;
            }
          )?.users ?? [];

        lookupResults.push({
          company,
          users: users.map((u) => ({
            id: u.id,
            email: u.email ?? "(no email)",
            created_at: u.created_at,
          })),
        });
      }

      lookupResults.sort((a, b) =>
        a.company.name.localeCompare(b.company.name),
      );
      setResults(lookupResults);
      setMatchedUserEmails(hitUserEmails);

      if (lookupResults.length === 0) {
        message.info("No matching companies found.");
        setCurrentStep(0);
      } else if (lookupResults.length === 1) {
        setSelectedCompanyId(lookupResults[0].company.id);
        setCurrentStep(1);
      } else {
        setCurrentStep(1);
      }
    } catch (err) {
      console.error("Bot lookup failed:", err);
      message.error(
        err instanceof Error
          ? err.message
          : "Lookup failed. Check admin access.",
      );
      setResults([]);
      setMatchedUserEmails([]);
      setCurrentStep(0);
    } finally {
      setLoading(false);
    }
  };

  const stepStatus = (
    index: number,
  ): "wait" | "process" | "finish" | "error" => {
    if (index < currentStep) return "finish";
    if (index === currentStep) return "process";
    return "wait";
  };

  return (
    <div style={{ padding: 16 }}>
      <AdminNavBar />

      <Space align="center" style={{ marginBottom: 8 }}>
        <RobotOutlined style={{ fontSize: 28, color: "#1890ff" }} />
        <Title level={2} style={{ margin: 0 }}>
          Bot
        </Title>
      </Space>

      <Paragraph type="secondary" style={{ marginBottom: 12 }}>
        Demo pipeline and sample conversation for a future Gmail / WhatsApp
        integration. The API docs describe the planned partner data endpoints
        (dashboard read access — not an LLM API).
      </Paragraph>

      <Segmented
        style={{ marginBottom: 16 }}
        value={adminView}
        onChange={(value) => setAdminView(value as BotAdminView)}
        options={[
          {
            label: "Pipeline steps",
            value: "pipeline",
            icon: <ApartmentOutlined />,
          },
          {
            label: "Full sample conversation",
            value: "sample",
            icon: <MessageOutlined />,
          },
          {
            label: "API docs",
            value: "api",
            icon: <ApiOutlined />,
          },
          {
            label: "Keys & logs",
            value: "keys",
            icon: <KeyOutlined />,
          },
          {
            label: "Glossary",
            value: "glossary",
            icon: <BookOutlined />,
          },
        ]}
      />

      {adminView === "sample" && <BotSampleConversation />}

      {adminView === "api" && <PartnerApiDocs />}

      {adminView === "glossary" && <PartnerApiGlossary />}

      {adminView === "keys" && (
        <div>
          <PartnerApiKeysPanel />
          <PartnerApiRequestLogsPanel />
        </div>
      )}

      {adminView === "pipeline" && (
        <>
      <Card style={{ marginBottom: 24 }} styles={{ body: { paddingBottom: 8 } }}>
        <Steps
          current={currentStep}
          size="small"
          onChange={(step) => {
            if (step === 0) setCurrentStep(0);
            if (step === 1 && searched) setCurrentStep(1);
            if (step >= 2 && selectedCompanyId != null) setCurrentStep(step);
          }}
          items={PIPELINE_STEPS.map((s, index) => ({
            title: s.title,
            description: (
              <Space direction="vertical" size={0}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {s.subtitle}
                </Text>
                <Text
                  code
                  style={{
                    fontSize: 11,
                    opacity: s.status === "planned" ? 0.65 : 1,
                  }}
                >
                  {s.endpointHint}
                </Text>
              </Space>
            ),
            icon: s.icon,
            status:
              s.status === "planned" && index === currentStep
                ? "process"
                : stepStatus(index),
          }))}
        />
      </Card>

      {currentStep === 0 && (
        <BotResolveStep
          form={form}
          loading={loading}
          searched={searched}
          hasResults={results.length > 0}
          onSearch={handleSearch}
        />
      )}

      {currentStep === 1 && (
        <BotCompanyStep
          searched={searched}
          results={results}
          matchedUserEmails={matchedUserEmails}
          selectedCompanyId={selectedCompanyId}
          onSelectCompany={setSelectedCompanyId}
          onBack={() => setCurrentStep(0)}
          onContinue={() => {
            setBookingQuery("");
            setStatusFilter(null);
            setInsightsMode("lookup");
            resetBookingState();
            setCurrentStep(2);
          }}
        />
      )}

      {currentStep === 2 && (
        <BotInsightsStep
          selectedResult={selectedResult}
          selectedCompanyId={selectedCompanyId}
          mode={insightsMode}
          onModeChange={handleInsightsModeChange}
          bookingQuery={bookingQuery}
          onBookingQueryChange={setBookingQuery}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          bookingLoading={bookingLoading}
          listLoading={listLoading}
          bookingSearched={bookingSearched}
          bookingInsight={bookingInsight}
          bookingList={bookingList}
          onLookup={handleBookingLookup}
          onLoadRecent={handleLoadRecent}
          onLoadAll={handleLoadAll}
          onLoadByStatus={handleLoadByStatus}
          onSelectListBooking={handleSelectListBooking}
          onBack={() => setCurrentStep(1)}
          onContinue={() => setCurrentStep(3)}
        />
      )}

      {currentStep === 3 && (
        <BotReplyStep
          selectedResult={selectedResult}
          selectedCompanyId={selectedCompanyId}
          bookingInsight={bookingInsight}
          onBack={() => setCurrentStep(2)}
        />
      )}
        </>
      )}
    </div>
  );
};

export default BotAdmin;
